/**
 * Case settings for a linked case: the employer comes from the company
 * record, read-only here, with Open company and Unlink.
 */
import { ArrowUpRight, Unlink } from "lucide-react";
import type { FC, ReactNode } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";

import { companyActions } from "../actions";
import { companyPath } from "../routes";
import type { CaseCompanyContext, Company } from "../types";
import { formatAddress } from "../utils";
import { CompanyMark } from "./CompanyMark";

const Row: FC<{ k: string; children: ReactNode }> = ({ k, children }) => (
  <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-3 py-1.5 text-base">
    <span className="text-fg-3">{k}</span>
    <span className="min-w-0">{children || "–"}</span>
  </div>
);

type Props = {
  individualId: string;
  company: Company;
  link: CaseCompanyContext["link"];
};

export const LinkedCompanyCard: FC<Props> = ({
  individualId,
  company,
  link
}) => {
  const entity = link?.entity;
  const signatory = entity?.signatory;
  return (
    <div
      className="flex max-w-2xl flex-col gap-3 rounded-lg border border-line-2 p-4"
      data-testid="linked-company"
    >
      <div className="flex items-center gap-3">
        <CompanyMark name={company.name} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-medium">{company.name}</span>
          <span className="text-sm text-fg-3">
            The employer on this case comes from the company record. Edit it
            there and every linked case updates.
          </span>
        </span>
        <Button asChild size="sm" variant="secondary">
          <Link to={companyPath(company.id)}>
            <ArrowUpRight /> Open company
          </Link>
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            void companyActions.unlink(company, [individualId], {
              entity_id: entity?.id ?? null,
              job_description_id: link?.job_description?.id ?? null,
              worksite_id: link?.worksite?.id ?? null
            })
          }
        >
          <Unlink /> Unlink
        </Button>
      </div>
      <div className="border-t border-line-2 pt-2">
        <Row k="Legal business name">{entity?.legal_name}</Row>
        {entity?.dba ? <Row k="DBA">{entity.dba}</Row> : null}
        <Row k="FEIN">{entity?.fein}</Row>
        <Row k="Business address">{formatAddress(entity?.address)}</Row>
        <Row k="Signatory">
          {signatory?.name
            ? [signatory.name, signatory.title, signatory.email]
                .filter(Boolean)
                .join(" · ")
            : ""}
        </Row>
        {link?.job_description ? (
          <Row k="Job">{link.job_description.title}</Row>
        ) : null}
        {link?.worksite ? <Row k="Worksite">{link.worksite.name}</Row> : null}
      </div>
    </div>
  );
};
